import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

import { LiPeopleType } from "@/enum/li-people.enum";

import { LiPeopleEntrySchema } from "./liPeopleEntry";

const { REQUIRED, INVALID_URL } = ERROR_TYPE;

export const SuggestionSchema = z.object({
	urn: z.string(),
	title: z.string(),
	subtitle: z.string().optional(),
	image: z.string().optional(),
});

export const SuggestionsSchema = z.object({
	suggestions: z.array(SuggestionSchema),
});

export const EnrichedSuggestionSchema = z.object({
	id: z.string(),
	title: z.string(),
	subtitle: z.string().optional(),
	image: z.string().optional(),
});

export const EnrichedSuggestionsSchema = z.object({
	suggestions: z.array(EnrichedSuggestionSchema),
});

export const PersonSearchSchema = z.object({
	fullName: z.string(),
	primarySubtitle: z.string().optional(),
	secondarySubtitle: z.string().optional(),
	profilePicture: z.string().optional(),
	navigationUrl: z.string(),
	summary: z.string().optional(),
});

export const PersonSearchesSchema = z.object({
	people: z.array(PersonSearchSchema),
	totalResults: z.number(),
});

export const LiPeopleSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	keyword: z.string().optional().nullable(),
	titleFreeText: z.string().optional().nullable(),
	companyFreeText: z.string().optional().nullable(),
	firstName: z.string().optional().nullable(),
	lastName: z.string().optional().nullable(),
	locationList: z.array(SuggestionSchema).optional().nullable(),
	languageList: z.array(SuggestionSchema).optional().nullable(),
	serviceCategoryList: z.array(SuggestionSchema).optional().nullable(),
	schoolFreeText: z.string().optional().nullable(),
	industryList: z.array(SuggestionSchema).optional().nullable(),
	schoolList: z.array(SuggestionSchema).optional().nullable(),
	follows: PersonSearchSchema.optional().nullable(),
	liPeopleEntries: z.array(LiPeopleEntrySchema).optional(),
	type: z.nativeEnum(LiPeopleType),
	listId: z.number().optional(),
	firstConnection: z.boolean().optional(),
	secondConnection: z.boolean().optional(),
	thirdConnection: z.boolean().optional(),
	plannedScrapingUserId: z.number().optional().nullable(),
	currentCompaniesList: z
		.array(
			z.object({
				urn: z.string(),
				title: z.string(),
				image: z.string().optional(),
				subtitle: z.string().optional(),
			})
		)
		.optional()
		.nullable(),
	pastCompaniesList: z
		.array(
			z.object({
				urn: z.string(),
				title: z.string(),
				image: z.string().optional(),
				subtitle: z.string().optional(),
			})
		)
		.optional()
		.nullable(),
	followerOf: z
		.object({
			urn: z.string(),
			title: z.string(),
			image: z.string().optional(),
			subtitle: z.string().optional(),
		})
		.optional()
		.nullable(),
	connectionsOf: z
		.object({
			urn: z.string(),
			title: z.string(),
			image: z.string().optional(),
			subtitle: z.string().optional(),
		})
		.optional()
		.nullable(),
});

export const UpdateLiPeopleSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	urls: z.array(z.string()),
	listId: z.number().optional(),
});

export const LiPeopleResponseSchema = z.object({
	data: z.array(LiPeopleSchema),
	meta: MetaDataSchema,
});

export const CreateLiPeopleSchema = z
	.object({
		id: z.number().optional(),
		name: z.string().min(4, { message: REQUIRED }).trim(),
		urls: z.string().nullable().optional(),
		keyword: z.string().optional().nullable(),
		titleFreeText: z.string().optional().nullable(),
		companyFreeText: z.string().optional().nullable(),
		firstName: z.string().optional().nullable(),
		lastName: z.string().optional().nullable(),
		locationList: z.array(SuggestionSchema).optional().nullable(),
		languageList: z.array(SuggestionSchema).optional().nullable(),
		serviceCategoryList: z.array(SuggestionSchema).optional().nullable(),
		schoolFreeText: z.string().optional().nullable(),
		industryList: z.array(SuggestionSchema).optional().nullable(),
		schoolList: z.array(SuggestionSchema).optional().nullable(),
		follows: PersonSearchSchema.optional().nullable(),
		type: z.nativeEnum(LiPeopleType),
		listId: z.number().optional(),
		firstConnection: z.boolean(),
		secondConnection: z.boolean(),
		thirdConnection: z.boolean(),
		plannedScrapingUserId: z.number().optional().nullable(),
		currentCompaniesList: z
			.array(
				z.object({
					urn: z.string(),
					title: z.string(),
					image: z.string().optional(),
					subtitle: z.string().optional(),
				})
			)
			.optional()
			.nullable(),
		pastCompaniesList: z
			.array(
				z.object({
					urn: z.string(),
					title: z.string(),
					image: z.string().optional(),
					subtitle: z.string().optional(),
				})
			)
			.optional()
			.nullable(),
		followerOf: z
			.object({
				urn: z.string(),
				title: z.string(),
				image: z.string().optional(),
				subtitle: z.string().optional(),
			})
			.optional()
			.nullable(),
	})
	.superRefine(({ urls, type, keyword }, ctx) => {
		if (type === LiPeopleType.SEARCH && !keyword) {
			ctx.addIssue({
				code: "custom",
				path: ["keyword"],
				message: REQUIRED,
			});
		}

		if (type === LiPeopleType.SPECIFIC && urls?.length === 0) {
			ctx.addIssue({
				code: "custom",
				path: ["urls"],
				message: REQUIRED,
			});
		}

		if (
			type === LiPeopleType.SPECIFIC &&
			!urls?.toLowerCase()?.includes("www.linkedin.com/search/results/people/")
		) {
			ctx.addIssue({
				code: "custom",
				path: ["urls"],
				message: INVALID_URL,
			});
		}
	});

export type LiPeople = z.infer<typeof LiPeopleSchema>;

export type Suggestion = z.infer<typeof SuggestionSchema>;
export type Suggestions = z.infer<typeof SuggestionsSchema>;

export type EnrichedSuggestion = z.infer<typeof EnrichedSuggestionSchema>;
export type EnrichedSuggestions = z.infer<typeof EnrichedSuggestionsSchema>;

export type PersonSearch = z.infer<typeof PersonSearchSchema>;
export type PersonSearches = z.infer<typeof PersonSearchesSchema>;

export type CreateLiPeople = z.infer<typeof CreateLiPeopleSchema>;

export type UpdateLiPeople = z.infer<typeof UpdateLiPeopleSchema>;

export type LiPeopleResponse = z.infer<typeof LiPeopleResponseSchema>;
